import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  ExternalLink,
  BookOpen,
  Users,
  Award
} from 'lucide-react';

const FooterContainer = styled.footer`
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-2xl) var(--spacing-lg) var(--spacing-lg);
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-2xl);
  margin-bottom: var(--spacing-2xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
`;

const FooterSection = styled.div`
  h3 {
    color: var(--color-text-primary);
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: var(--spacing-md);
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    
    svg {
      width: 20px;
      height: 20px;
      color: var(--color-primary);
    }
  }
  
  p {
    color: var(--color-text-muted);
    line-height: 1.6;
    margin-bottom: var(--spacing-md);
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  
  li {
    margin-bottom: var(--spacing-sm);
  }
  
  a {
    color: var(--color-text-secondary);
    text-decoration: none;
    transition: color var(--animation-duration) ease;
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    
    &:hover {
      color: var(--color-primary);
      text-decoration: none;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid var(--color-border);
  padding-top: var(--spacing-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  .heart {
    color: #ef4444;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`;

const FooterLinks2 = styled.div`
  display: flex;
  gap: var(--spacing-lg);
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  a {
    color: var(--color-text-muted);
    text-decoration: none;
    font-size: 0.875rem;
    transition: color var(--animation-duration) ease;
    
    &:hover {
      color: var(--color-primary);
    }
  }
`;

// 统计卡片已删除，不再需要样式

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <h3>
              <BookOpen />
              关于项目
            </h3>
            <p>
              历史小课堂是一个专注于中华历史文化传承的可视化学习项目。
              通过现代化的技术手段，让历史学习变得更加生动有趣。
            </p>
            {/* 已移除底部统计卡片 */}
          </FooterSection>

          <FooterSection>
            <h3>
              <Users />
              快速导航
            </h3>
            <FooterLinks>
              <li>
                <Link to="/">
                  首页
                </Link>
              </li>
              <li>
                <Link to="/timeline">
                  历史时间线
                </Link>
              </li>
              <li>
                <Link to="/quiz">
                  知识测验
                </Link>
              </li>
              <li>
                <Link to="/leaderboard">
                  排行榜
                </Link>
              </li>
              <li>
                <Link to="/profile">
                  个人中心
                </Link>
              </li>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <h3>
              <Award />
              学习资源
            </h3>
            <FooterLinks>
              <li>
                <a href="https://zh.wikipedia.org/zh-cn/%E4%B8%AD%E5%9B%BD%E5%8E%86%E5%8F%B2" target="_blank" rel="noopener noreferrer">
                  历史百科
                  <ExternalLink />
                </a>
              </li>
              <li>
                <a href="https://www.nlc.cn/" target="_blank" rel="noopener noreferrer">
                  图书馆馆藏
                  <ExternalLink />
                </a>
              </li>
              <li>
                <a href="https://www.dpm.org.cn/" target="_blank" rel="noopener noreferrer">
                  故宫博物院
                  <ExternalLink />
                </a>
              </li>
              <li>
                <a href="https://www.chnmuseum.cn/" target="_blank" rel="noopener noreferrer">
                  历史博物馆
                  <ExternalLink />
                </a>
              </li>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <h3>
              联系我们
            </h3>
            <p>
              如果您有任何问题或建议，欢迎与我们联系。
              我们致力于为用户提供最好的学习体验。
            </p>
            {/* 已移除邮箱与 GitHub 链接 */}
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <Copyright>
            © {currentYear} 历史小课堂
          </Copyright>
          
          <FooterLinks2>
            {/* 仅保留 ICP 备案信息 */}
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备2025491173号-1</a>
          </FooterLinks2>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
